import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Users, CreditCard, BarChart3, Settings, Shield, LayoutDashboard, X, Menu, Coins, LogOut } from 'lucide-react';
import { useAuth } from '../../auth';
import { route } from '../../../shared/utils';

const GOLD = '#FFD93D';

const cardStyle: React.CSSProperties = {
  background: '#1e1e1e',
  border: '1px solid #2a2a2a',
  borderRadius: 12,
};

const ALL_LINKS = [
  { label: 'Dashboard', href: '/admin', icon: LayoutDashboard, adminOnly: false },
  { label: 'User Management', href: '/user/admin', icon: Users, adminOnly: false },
  { label: 'Services & Pricing', href: '/admin/services', icon: BarChart3, adminOnly: false },
  { label: 'Credit Log', href: '/admin/credits', icon: Coins, adminOnly: false },
  { label: 'Credit Packs', href: '/admin/packs', icon: CreditCard, adminOnly: true },
  { label: 'App Settings', href: '/admin/settings', icon: Settings, adminOnly: true },
  { label: 'Audit Logs', href: '/admin/audit', icon: Shield, adminOnly: false },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export const AdminSidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isAdminSystem = user?.role === 'admin' && user?.level === 'system';

  const links = ALL_LINKS.filter(l => !l.adminOnly || isAdminSystem);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  if (isMobile) {
    return (
      <>
        {/* Bottom bar */}
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 90,
          background: '#111', borderTop: '1px solid #2a2a2a',
          display: 'flex', alignItems: 'center', justifyContent: 'space-around',
          padding: '8px 0 max(8px, env(safe-area-inset-bottom))',
          height: 60,
        }}>
          {links.slice(0, 4).map((link) => {
            const isActive = location.pathname === route(link.href);
            return (
              <a key={link.href} href={route(link.href)} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                textDecoration: 'none', flex: 1, padding: '4px 0',
              }}>
                <link.icon size={20} style={{ color: isActive ? GOLD : '#555' }} />
                <span style={{ fontSize: 9, color: isActive ? GOLD : '#555', fontWeight: isActive ? 600 : 400 }}>
                  {link.label.split(' ')[0]}
                </span>
              </a>
            );
          })}
          {/* More button */}
          <button onClick={() => setDrawerOpen(true)} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            background: 'none', border: 'none', cursor: 'pointer', flex: 1, padding: '4px 0',
          }}>
            <Menu size={20} style={{ color: '#555' }} />
            <span style={{ fontSize: 9, color: '#555' }}>More</span>
          </button>
        </div>

        {/* Bottom sheet backdrop */}
        {drawerOpen && (
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 100,
              background: 'rgba(0,0,0,0.6)',
              animation: 'fadeIn 0.2s ease',
            }}
          />
        )}

        {/* Bottom sheet drawer */}
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 101,
          background: '#1a1a1a', borderTop: '1px solid #333',
          borderRadius: '16px 16px 0 0',
          transform: drawerOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
        }}>
          {/* Handle bar */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
            <div style={{ width: 36, height: 4, background: '#444', borderRadius: 2 }} />
          </div>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 20px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                background: 'rgba(255,217,61,0.15)', border: '1.5px solid rgba(255,217,61,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, color: GOLD,
              }}>
                {(user?.name || user?.email || '?')[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#eee' }}>{user?.name || 'Admin'}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: GOLD }}>{user?.role}.{user?.level}</div>
              </div>
            </div>
            <button onClick={() => setDrawerOpen(false)} style={{
              background: '#2a2a2a', border: 'none', borderRadius: 8,
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#888',
            }}>
              <X size={16} />
            </button>
          </div>

          <div style={{ borderTop: '1px solid #2a2a2a' }} />

          {/* All links */}
          {links.map((link, _i) => {
            const isActive = location.pathname === route(link.href);
            return (
              <a key={link.href} href={route(link.href)} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 20px',
                borderBottom: '1px solid #242424',
                textDecoration: 'none',
                background: isActive ? 'rgba(255,217,61,0.06)' : 'transparent',
                color: isActive ? GOLD : '#ccc',
                fontSize: 14, fontWeight: isActive ? 600 : 400,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: isActive ? 'rgba(255,217,61,0.12)' : '#242424',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <link.icon size={17} style={{ color: isActive ? GOLD : '#888' }} />
                </div>
                {link.label}
                {isActive && (
                  <div style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: GOLD }} />
                )}
              </a>
            );
          })}
          {/* Logout */}
          <button onClick={() => logout()} style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '14px 20px', width: '100%',
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: '#ef4444', fontSize: 14, fontWeight: 400,
          }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LogOut size={17} style={{ color: '#ef4444' }} />
            </div>
            Sign Out
          </button>
        </div>

        <style>{`
          @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        `}</style>
      </>
    );
  }

  // Desktop sidebar
  return (
    <div style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Profile card */}
      <div style={{ ...cardStyle, padding: '16px 14px' }}>
        {/* Avatar + name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(255,217,61,0.15)', border: '1.5px solid rgba(255,217,61,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 700, color: GOLD,
          }}>
            {(user?.name || user?.email || '?')[0].toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#eee', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name || 'Admin'}
            </div>
            <div style={{ fontSize: 11, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.email}
            </div>
          </div>
        </div>

        {/* Role badge */}
        <div style={{ background: 'rgba(255,217,61,0.08)', borderRadius: 8, padding: '6px 10px', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Role</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: GOLD }}>{user?.role}.{user?.level}</span>
        </div>

        {/* Logout button */}
        <button
          onClick={() => logout()}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            padding: '8px 0', borderRadius: 8, border: '1px solid #2a2a2a',
            background: 'transparent', color: '#888', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)'; e.currentTarget.style.color = '#ef4444'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}
        >
          <LogOut size={13} />
          Sign Out
        </button>
      </div>

      {/* Nav links */}
      <div style={{ ...cardStyle, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px 8px', borderBottom: '1px solid #2a2a2a' }}>
          <span style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Management</span>
        </div>
        {links.map((link, _i) => {
          const isActive = location.pathname === route(link.href);
          return (
            <a
              key={link.href}
              href={route(link.href)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '11px 16px',
                borderBottom: _i < links.length - 1 ? '1px solid #242424' : 'none',
                textDecoration: 'none',
                color: isActive ? GOLD : '#ccc',
                fontSize: 13, fontWeight: isActive ? 600 : 500,
                background: isActive ? 'rgba(255,217,61,0.06)' : 'transparent',
                transition: 'background 0.15s, color 0.15s',
              }}
              onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = '#252520'; e.currentTarget.style.color = GOLD; } }}
              onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ccc'; } }}
            >
              <link.icon size={15} style={{ color: GOLD, flexShrink: 0 }} />
              {link.label}
            </a>
          );
        })}
      </div>
    </div>
  );
};
